import { useStore } from '@nanostores/react';
import { ArrowRightToLine, Bug } from 'lucide-react';
import { useCallback, useEffect, useMemo } from 'react';

import { useCarePatientViewersQuery } from '@/entities/care-patients';
import { useMyStatusQuery } from '@/entities/me';
import { Button } from '@/shared/ui/shadcn/button';
import {
  $copilotCollapsed,
  isCopilotTabId,
  openCopilotOverlay,
  setCopilotActiveTab,
  toggleCopilotPanel,
} from '@/shared/stores/ui';
import { clearDebugEvents } from '@/features/copilot-debug';
import { useCopilotSession } from '@/features/copilot/lib';
import { useCopilotToolPartRenderers } from '@/features/copilot/tooling/useCopilotToolPartRenderers';
import { CopilotToolUiProvider } from '@/features/copilot/ui/ToolUi/CopilotToolUiProvider';
import { ThreadPopover } from '@/features/copilot/ui/ThreadPopover';

import { CopilotContentOutlet } from './CopilotContentOutlet';
import { CopilotDock } from './CopilotDock';
import { CopilotHeader } from './CopilotHeader';
import { useCopilotPanelUiState } from './useCopilotPanelUiState';

interface CopilotPanelProps {
  userId: string;
}

const COPILOT_PANEL_SOURCE = 'copilot-panel:composer';

export function CopilotPanel({ userId }: CopilotPanelProps) {
  const { activeTab, effectiveComposerMode } = useCopilotPanelUiState();
  const isCollapsed = useStore($copilotCollapsed);
  const { data: myStatus } = useMyStatusQuery();
  const canOpenAiDebug = myStatus?.subsystemAccess?.['evaluation_admin'] === true;
  const viewersQuery = useCarePatientViewersQuery(userId);
  const viewers = useMemo(
    () => viewersQuery.data?.viewers.map((viewer) => ({ name: viewer.display_name })) ?? [],
    [viewersQuery.data?.viewers]
  );

  useEffect(() => {
    clearDebugEvents();
  }, [userId]);

  const { chat, threads, sendText } = useCopilotSession();
  const { messages, stop, status, addToolOutput, setMessages } = chat;
  const isBusy = status === 'submitted' || status === 'streaming';
  const showPlaceholderContent =
    activeTab === 'copilot' && messages.length === 0 && status === 'ready';
  const toolPartRenderers = useCopilotToolPartRenderers();

  const handleSwitchThread = useCallback(
    (threadId: string) => {
      stop();
      void threads.switchThread(threadId, messages, setMessages);
    },
    [stop, threads, messages, setMessages]
  );

  const handleNewThread = useCallback(() => {
    stop();
    void threads.createNewThread(messages, setMessages);
  }, [stop, threads, messages, setMessages]);

  const handleSendCopilotMessage = useCallback(
    (text: string) => {
      sendText({ text, source: COPILOT_PANEL_SOURCE });
    },
    [sendText]
  );

  if (isCollapsed) {
    return (
      <div className="flex h-full items-start justify-center pt-2">
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={toggleCopilotPanel}
          aria-label="Expand panel"
        >
          <ArrowRightToLine className="h-4 w-4 rotate-180" />
        </Button>
      </div>
    );
  }

  return (
    <CopilotToolUiProvider
      toolPartRenderers={toolPartRenderers}
      addToolOutput={addToolOutput}
      patientId={userId}
      onSendMessage={handleSendCopilotMessage}
    >
      <div className="relative flex h-full flex-col">
        <div className="shrink-0">
          <CopilotHeader
            activeTab={activeTab}
            onTabChange={(tabId) => {
              if (!isCopilotTabId(tabId)) return;
              setCopilotActiveTab(tabId);
            }}
            isCollapsed={isCollapsed}
            viewers={viewers}
            onToggleCollapsed={toggleCopilotPanel}
            extraActions={
              <>
                {canOpenAiDebug ? (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => openCopilotOverlay('aiDebug', null)}
                    aria-label="Open AI debug panel"
                  >
                    <Bug className="h-4 w-4" />
                  </Button>
                ) : null}
                <ThreadPopover
                  threads={threads.threads}
                  activeThreadId={threads.activeThreadId}
                  isLoading={threads.isLoading}
                  onSwitchThread={handleSwitchThread}
                  onNewThread={handleNewThread}
                />
              </>
            }
          />
        </div>

        <div className="relative flex-1 overflow-hidden">
          <CopilotContentOutlet activeTab={activeTab} messages={messages} status={status} />
        </div>

        <CopilotDock
          userId={userId}
          effectiveComposerMode={effectiveComposerMode}
          isBusy={isBusy}
          showQuickActions={showPlaceholderContent}
          onStop={() => stop()}
          onSendMessage={handleSendCopilotMessage}
        />
      </div>
    </CopilotToolUiProvider>
  );
}
